import { Injectable } from '@angular/core';
import { PermissionsService } from '../permissions/permissions.service';
import { ROUTES } from '../navigation/app-navigation-items';
import { RouteInfo } from '../navigation/app-navigation.metadata';
import { APP_PRIMARY_COMMANDS, AppNavigationShortcut } from '../navigation/app-navigation-shortcuts';
import { CommandPaletteItem } from './command-palette.models';

@Injectable({ providedIn: 'root' })
export class CommandPaletteRegistryService {
  private readonly defaultCommands = APP_PRIMARY_COMMANDS.map((command) => this.fromShortcut(command));

  constructor(private readonly permissionsService: PermissionsService) {}

  getVisibleCommands(): CommandPaletteItem[] {
    const commands = [...this.defaultCommands, ...this.flattenRoutes(ROUTES)];
    const unique = new Map<string, CommandPaletteItem>();

    commands
      .filter((command) => this.isValidCommand(command))
      .filter((command) => this.permissionsService.hasAny(command.permission || []))
      .forEach((command) => {
        const key = `${command.route}:${command.label}`.toLowerCase();
        if (!unique.has(key)) {
          unique.set(key, command);
        }
      });

    return [...unique.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  search(query: string): CommandPaletteItem[] {
    const normalizedQuery = this.normalize(query);
    const commands = this.getVisibleCommands();
    if (!normalizedQuery) {
      return commands.slice(0, 12);
    }

    return commands
      .map((command) => ({ command, score: this.scoreCommand(command, normalizedQuery) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.command.label.localeCompare(b.command.label))
      .slice(0, 20)
      .map((entry) => entry.command);
  }

  private flattenRoutes(items: RouteInfo[], group = 'Navigation'): CommandPaletteItem[] {
    const commands: CommandPaletteItem[] = [];

    items.forEach((item) => {
      const nextGroup = item.groupTitle && item.title ? item.title : group;
      if (!item.groupTitle && item.path) {
        commands.push(this.createCommand(
          this.slug(`${nextGroup}-${item.title}-${item.path}`),
          item.title,
          item.path,
          item.icon || 'arrow_forward',
          nextGroup,
          item.permission
        ));
      }
      if (Array.isArray(item.submenu) && item.submenu.length) {
        commands.push(...this.flattenRoutes(item.submenu, nextGroup));
      }
    });

    return commands;
  }

  private fromShortcut(command: AppNavigationShortcut): CommandPaletteItem {
    return this.createCommand(
      command.id,
      command.label,
      command.route,
      command.icon,
      command.group,
      command.permission
    );
  }

  private createCommand(
    id: string,
    label: string,
    route: string,
    icon: string,
    group: string,
    permission: string[] = []
  ): CommandPaletteItem {
    return {
      id,
      label,
      route,
      icon,
      group,
      permission,
      keywords: this.normalize(`${label} ${route} ${group}`)
    };
  }

  private isValidCommand(command: CommandPaletteItem): boolean {
    const route = `${command.route || ''}`.trim();
    return !!command.label && !!route && !route.startsWith('http') && route !== '#';
  }

  private scoreCommand(command: CommandPaletteItem, query: string): number {
    const label = this.normalize(command.label);
    const route = this.normalize(command.route);
    const group = this.normalize(command.group);
    const keywords = command.keywords || this.normalize(`${label} ${route} ${group}`);

    if (label === query) return 100;
    if (label.startsWith(query)) return 80;
    if (keywords.includes(query)) return 50;
    return query.split(' ').every((part) => keywords.includes(part)) ? 30 : 0;
  }

  private normalize(value: string): string {
    return `${value || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private slug(value: string): string {
    return this.normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
}
